/**
 * Loyalty Program Page - Points, rewards, and tier status
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ArrowLeft,
  Gift,
  Star,
  Zap,
  Trophy,
  ChevronRight,
  Sparkles,
  Truck,
  Percent,
  Clock,
  CheckCircle,
  Crown
} from "lucide-react";

const tierColors = {
  bronze: { bg: "bg-amber-700", text: "text-amber-100", border: "border-amber-600" },
  silver: { bg: "bg-slate-400", text: "text-slate-900", border: "border-slate-300" },
  gold: { bg: "bg-yellow-500", text: "text-yellow-900", border: "border-yellow-400" },
  platinum: { bg: "bg-purple-600", text: "text-purple-100", border: "border-purple-400" }
};

const LoyaltyProgram = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);
  const [history, setHistory] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [redeeming, setRedeeming] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, historyRes, vouchersRes] = await Promise.all([
        api.get("/loyalty/me"),
        api.get("/loyalty/history?limit=10"),
        api.get("/loyalty/vouchers?used=false")
      ]);
      
      setLoyalty(statusRes.data);
      setHistory(historyRes.data?.history || []);
      setVouchers(vouchersRes.data?.vouchers || []);
    } catch (error) {
      console.error("Failed to fetch loyalty data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRedeem = async (rewardType, points) => {
    if (loyalty.points < points) {
      toast.error("Not enough points");
      return;
    }
    
    setRedeeming(true);
    try {
      const response = await api.post("/loyalty/redeem", {
        points: points,
        reward_type: rewardType
      });
      
      toast.success("Reward redeemed successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tier = loyalty?.tier || "bronze";
  const colors = tierColors[tier];
  const progressPercent = loyalty?.points_to_next_tier > 0 
    ? Math.min(100, ((loyalty.lifetime_points || 0) / (loyalty.lifetime_points + loyalty.points_to_next_tier)) * 100)
    : 100;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="loyalty-program">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-purple-700 px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="p-2 text-purple-200 hover:text-white hover:bg-purple-800"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Liantiao Rewards</h1>
              <p className="text-sm text-purple-200">Earn points, unlock rewards</p>
            </div>
          </div>

          {/* Tier Card */}
          <div className={"rounded-2xl p-6 " + colors.bg + " " + colors.border + " border-2"}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Crown className={"h-8 w-8 " + colors.text} />
                </div>
                <div>
                  <p className={"text-sm opacity-80 " + colors.text}>Your Tier</p>
                  <h2 className={"text-2xl font-bold " + colors.text}>{loyalty?.tier_name || "Bronze"}</h2>
                </div>
              </div>
              <div className="text-right">
                <p className={"text-3xl font-bold " + colors.text}>{loyalty?.points?.toLocaleString() || 0}</p>
                <p className={"text-sm opacity-80 " + colors.text}>Available Points</p>
              </div>
            </div>

            {/* Progress to next tier */}
            {loyalty?.next_tier && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className={colors.text + " opacity-80"}>Progress to {loyalty.next_tier}</span>
                  <span className={colors.text}>{loyalty.points_to_next_tier} pts needed</span>
                </div>
                <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white rounded-full transition-all"
                    style={{ width: progressPercent + "%" }}
                  />
                </div>
              </div>
            )}

            {/* Perks */}
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className={colors.text + " text-sm mb-2 opacity-80"}>Your Perks:</p>
              <div className="flex flex-wrap gap-2">
                {loyalty?.tier_perks?.map((perk, idx) => (
                  <span key={idx} className="px-3 py-1 bg-white/20 rounded-full text-sm text-white">
                    {perk}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Multiplier Info */}
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 flex items-center gap-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Zap className="h-6 w-6 text-purple-400" />
          </div>
          <div>
            <p className="font-medium text-white">{loyalty?.multiplier || 1}x Points Multiplier</p>
            <p className="text-sm text-slate-400">Earn {loyalty?.multiplier || 1} point per J$100 spent</p>
          </div>
        </div>

        {/* Redeem Rewards */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Gift className="h-5 w-5 text-purple-400" />
              Redeem Rewards
            </h2>
          </div>
          <div className="p-4 grid gap-3">
            {/* Discount */}
            <div className="bg-slate-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Percent className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="font-medium text-white">J$100 Discount</p>
                  <p className="text-sm text-slate-400">100 points</p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => handleRedeem("discount", 100)}
                disabled={redeeming || loyalty?.points < 100}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Redeem
              </Button>
            </div>

            {/* Free Delivery */}
            <div className="bg-slate-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Truck className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-white">Free Delivery</p>
                  <p className="text-sm text-slate-400">200 points</p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => handleRedeem("free_delivery", 200)}
                disabled={redeeming || loyalty?.points < 200}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Redeem
              </Button>
            </div>

            {/* Mystery Gift */}
            <div className="bg-slate-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Sparkles className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-white">Mystery Gift</p>
                  <p className="text-sm text-slate-400">500 points</p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => handleRedeem("gift", 500)}
                disabled={redeeming || loyalty?.points < 500}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Redeem
              </Button>
            </div>
          </div>
        </div>

        {/* Active Vouchers */}
        {vouchers.length > 0 && (
          <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-400" />
                Your Vouchers ({vouchers.length})
              </h2>
            </div>
            <div className="divide-y divide-slate-800">
              {vouchers.map((voucher) => (
                <div key={voucher.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">{voucher.description}</p>
                    <p className="text-sm text-slate-400">
                      {new Date(voucher.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Points History */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-400" />
              Points History
            </h2>
          </div>
          {history.length === 0 ? (
            <div className="p-8 text-center">
              <Trophy className="h-12 w-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-400">No activity yet</p>
              <p className="text-sm text-slate-500">Earn points by placing orders</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {history.map((item) => (
                <div key={item.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">{item.description}</p>
                    <p className="text-sm text-slate-400">
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={item.points > 0 ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>
                    {item.points > 0 ? "+" : ""}{item.points}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How to Earn */}
        <div className="bg-purple-900/30 border border-purple-800 rounded-xl p-4">
          <h3 className="font-bold text-purple-200 mb-3">How to Earn Points</h3>
          <ul className="space-y-2 text-sm text-purple-300">
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-purple-400" />
              Earn 1 point for every J$100 spent
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-purple-400" />
              Higher tiers earn more points per order
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-purple-400" />
              Points never expire!
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default LoyaltyProgram;
